import { type FC, useRef, useState } from "react";

import { formatDollars } from "@/shared/utils/formatters";
import { INVENTORY_CATEGORY_GROUPS } from "@/shared/constants/estateCategories";
import { sumByGroup } from "@/shared/utils/estateMath";
import { getGroupHex, DEBT_SEGMENT_HEX } from "@/shared/utils/categoryColors";

import type { EstateItem } from "@/shared/types/estateTypes";

import { DonutChart } from "@/shared/components/DonutChart";
import type { DonutSegment } from "@/shared/components/DonutChart";

const DEBT_STRIPE_COLOR = DEBT_SEGMENT_HEX;

interface LegendItemProps {
  color: string;
  label: string;
  isDebt?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

function LegendItem({ color, label, isDebt, onMouseEnter, onMouseLeave }: LegendItemProps) {
  return (
    <div
      className="flex w-fit cursor-default items-center gap-1.5"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <span
        className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
        style={{ backgroundColor: isDebt ? DEBT_STRIPE_COLOR : color }}
      />
      <span className="text-sm text-primary">{label}</span>
    </div>
  );
}

interface EstateBreakdownProps {
  assets: EstateItem[];
  debts: EstateItem[];
  netValue: number;
}

export const EstateBreakdown: FC<EstateBreakdownProps> = ({ assets, debts, netValue }) => {
  const [highlightedKey, setHighlightedKey] = useState<string | null>(null);
  const leaveTimer = useRef<ReturnType<typeof setTimeout>>(null);
  const formattedNet = formatDollars({ amount: netValue, rounding: "whole" });

  const allItems = [...assets, ...debts];
  const groupTotals = sumByGroup(allItems, INVENTORY_CATEGORY_GROUPS);

  const assetGroups = groupTotals.filter((g) => g.classification === "asset");
  const debtGroups = groupTotals.filter((g) => g.classification === "debt");
  const totalAssets = assetGroups.reduce((sum, g) => sum + g.value, 0);
  const totalDebts = debtGroups.reduce((sum, g) => sum + g.value, 0);

  const fmt = (v: number) => formatDollars({ amount: v, rounding: "whole" });

  const segments: DonutSegment[] = [
    ...assetGroups.map((g) => ({
      key: g.group,
      label: g.group,
      value: g.value,
      formattedValue: fmt(g.value),
      color: getGroupHex(g.group),
      type: "asset" as const,
    })),
    ...(totalDebts > 0
      ? [
          {
            key: "debts",
            label: "Debts",
            value: totalDebts,
            formattedValue: fmt(totalDebts),
            color: "",
            type: "debt" as const,
          },
        ]
      : []),
  ];

  const highlight = (key: string) => {
    if (leaveTimer.current) clearTimeout(leaveTimer.current);
    setHighlightedKey(key);
  };

  const clearHighlight = () => {
    leaveTimer.current = setTimeout(() => setHighlightedKey(null), 100);
  };

  return (
    <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-[50px]">
      <div className="flex items-center justify-center py-6">
        <DonutChart
          segments={segments}
          centerLabel="Net Estate Value"
          centerValue={formattedNet}
          highlightedKey={highlightedKey}
          size={255}
          strokeWidth={30}
        />
      </div>

      <div className="flex flex-col gap-6 lg:pl-6">
        {assetGroups.length > 0 && (
          <div className="flex flex-col gap-3">
            <p className="flex items-baseline gap-2 text-sm font-semibold">
              Assets <span className="text-sm font-normal text-secondary">{fmt(totalAssets)}</span>
            </p>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {assetGroups.map((g) => (
                <LegendItem
                  key={g.group}
                  color={getGroupHex(g.group)}
                  label={g.group}
                  onMouseEnter={() => highlight(g.group)}
                  onMouseLeave={clearHighlight}
                />
              ))}
            </div>
          </div>
        )}

        {debtGroups.length > 0 && <div className="border-t border-primary" />}

        {debtGroups.length > 0 && (
          <div className="flex flex-col gap-3">
            <p className="flex items-baseline gap-2 text-sm font-semibold">
              Debts <span className="text-sm font-normal text-secondary">{fmt(totalDebts)}</span>
            </p>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {debtGroups.map((g) => (
                <LegendItem
                  key={g.group}
                  color={getGroupHex(g.group)}
                  label={g.group}
                  isDebt
                  onMouseEnter={() => highlight("debts")}
                  onMouseLeave={clearHighlight}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
